import { useMemo } from 'react';
import { findStrategyForTab } from '../utils/strategyTabs';
import { findSmartList } from '../utils/listUrl';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

// Homepage (/) meta. Kept as constants so the crawler always sees a stable,
// correct title/description/canonical for the root URL regardless of which list
// happens to be shown there by default. See SEO INVARIANT in ListRouteSync.jsx.
const HOMEPAGE_TITLE = 'QuantApe | AI Stock Screener';
const HOMEPAGE_DESCRIPTION =
  "AI-powered stock screener with fundamentals, technicals, earnings & company intelligence. Because insight isn't optional. It's everything.";
const HOMEPAGE_URL = 'https://quantape.com/';

/**
 * ListSEO - Sets dynamic meta tags based on selected list
 *
 * Updates:
 * - <title> - "{List Name} | QuantApe"
 * - <meta name="description"> - List's short description
 * - <meta property="og:title/description"> - Same for social sharing
 * - <link rel="canonical"> - Current list URL
 *
 * SEO INVARIANT: whenever the URL path is exactly '/', emit the homepage meta
 * (not the meta of whatever list is defaulted into the homepage view). This is
 * what keeps Googlebot from indexing quantape.com/ with a list's title.
 */
const ListSEO = ({ selectedTab, categories, userListsData, strategies }) => {
  const { pathname } = useLocation();
  const isHomepage = pathname === '/';
  // Find the current filter/list metadata
  const listMeta = useMemo(() => {
    if (!selectedTab) return null;

    // Handle watchlist specially
    if (selectedTab === 'watchlist') {
      return {
        name: 'My Watchlist',
        description: 'Your personal stock watchlist on QuantApe',
        id: 'watchlist'
      };
    }

    // Search in categories
    if (categories) {
      const filter = findSmartList(categories, selectedTab); // includes nested (children) lists
      if (filter) {
        return {
          name: filter.name || selectedTab,
          description: filter.short_description || filter.description || filter.tooltip || '',
          id: selectedTab
        };
      }
    }

    // Search in user lists (custom filters, AI lists, portfolios)
    if (userListsData) {
      const userList = userListsData.find(list => list.id === selectedTab);
      if (userList) {
        return {
          name: userList.name || selectedTab,
          description: userList.description || `Custom list: ${userList.name}`,
          id: selectedTab
        };
      }
    }

    // Strategy tabs (issue #337): a strategy_<id> tab is named by the strategy row.
    const strategy = findStrategyForTab(strategies, selectedTab);
    if (strategy) {
      return {
        name: strategy.name,
        description: `Stocks opted into the ${strategy.name} strategy on QuantApe`,
        id: selectedTab
      };
    }

    // Fallback - format the ID as a name
    return {
      name: selectedTab.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      description: '',
      id: selectedTab
    };
  }, [selectedTab, categories, userListsData, strategies]);

  // Homepage (/) always emits the stable homepage meta — even before categories
  // load, and no matter which list is defaulted into the root view (watchlist for
  // logged-in users, HOMEPAGE_DEFAULT_TAB for anonymous). This is the durable
  // guard behind the SEO INVARIANT: the crawler sees homepage title/canonical at /.
  if (!isHomepage && !listMeta) return null;

  const title = isHomepage
    ? HOMEPAGE_TITLE
    : `${listMeta.name} | QuantApe AI Stock Screener`;

  const description = isHomepage
    ? HOMEPAGE_DESCRIPTION
    : (listMeta.description
      || `View ${listMeta.name} stocks on QuantApe. AI-powered stock screening with fundamentals, technicals, and company intelligence.`);

  const canonicalUrl = isHomepage
    ? HOMEPAGE_URL
    : `https://quantape.com/list/${listMeta.id}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />

      {/* Twitter */}
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:url" content={canonicalUrl} />
    </Helmet>
  );
};

export default ListSEO;
